import { CALENDAR } from '@/data/project'
import { PRIORITY_RANK, isOverdue, type Decision, type DecisionPriority } from '@/features/decisions/types'
import { OWNERS_DUE, isNamed, isSettled, type Owner } from '@/features/owners/types'
import type { Task } from '@/features/roadmap/types'
import { urlForKind, type UrlState } from '@/lib/urlState'

/**
 * The home page's two lists — what Missional Marketing owes us, and what Milestone has
 * moving — worked out from the live registers rather than from anything stored for them.
 *
 * Both cards used to read hand-maintained values out of the summary document: a line of
 * "gate pressure" from the plan, the phases somebody had marked current, and a
 * `workingOn` list nobody filled in. None of it moved when the work did. Everything here
 * is derived from the decisions, tasks and owners themselves, so an item appears the
 * moment it opens and leaves the moment it closes.
 */

export type ActionItem = {
  key: string
  kind: 'owner' | 'decision' | 'task'
  /** What the row leads with — a decision or task reference, or a role. */
  ref: string
  title: string
  /** Decisions carry one; an unowned role is treated as Critical; tasks carry none. */
  priority: DecisionPriority | null
  status: string
  /** `YYYY-MM-DD`, or null when the item has no date that could be past. */
  due: string | null
  /** A task's due as written, for the ones that are not a date — "ongoing", "Fridays". */
  dueText: string
  overdue: boolean
  /** Where clicking the row goes. */
  target: Partial<UrlState>
}

const MONTHS: Record<string, string> = {
  Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
  Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12',
}
const YEAR = CALENDAR[0]!.start.slice(0, 4)
const POST = CALENDAR.find((w) => w.id === 'Post')!

/**
 * A task's due, which is written by hand: `W3`, `W2-W3`, `Sep 11`, `Oct 12-27`, `post-W6`,
 * `ongoing`. A range is due at its end, because that is the deadline. Work with no end —
 * rolling, ongoing, every Friday — has no date, and so can never be past due.
 */
export function dueDateOf(due: string): string | null {
  const text = due.trim()
  if (/^post/i.test(text)) return POST.end

  const weeks = [...text.matchAll(/W(\d+)/g)]
  if (weeks.length > 0) {
    const last = `W${weeks[weeks.length - 1]![1]}`
    return CALENDAR.find((w) => w.id === last)?.end ?? null
  }

  const m = /^([A-Z][a-z]{2})\s+(\d{1,2})(?:\s*-\s*(\d{1,2}))?$/.exec(text)
  if (m && MONTHS[m[1]!]) return `${YEAR}-${MONTHS[m[1]!]}-${(m[3] ?? m[2])!.padStart(2, '0')}`
  return null
}

/** Past the end of its due day — the same test `isOverdue` applies to a decision. */
const pastDue = (due: string | null, today: Date) =>
  due !== null && new Date(`${due}T23:59:59`) < today

const isOpenTask = (t: Task) => t.status !== 'Done' && t.status !== 'Deferred'

function taskItem(t: Task, today: Date): ActionItem {
  const due = dueDateOf(t.due)
  return {
    key: `task:${t.id}`, kind: 'task', ref: t.id, title: t.task, priority: null,
    status: t.status, due, dueText: t.due, overdue: pastDue(due, today),
    target: urlForKind('task', t.id),
  }
}

/* A task carries no priority of its own. Sitting with High puts one that is waiting on the
   client above anything Medium, and below the Critical decisions everything hangs on. */
const rankOf = (i: ActionItem) => (i.priority ? PRIORITY_RANK[i.priority] : PRIORITY_RANK.High)

/** No date sorts last: something due on a day beats something due whenever. */
const byDue = (a: string | null, b: string | null) =>
  a === b ? 0 : a === null ? 1 : b === null ? -1 : a.localeCompare(b)

/**
 * Roles first — nothing else can start until each area has somebody who can decide — then
 * highest priority, then whatever is already late, then whatever is due soonest.
 */
function byUrgency(a: ActionItem, b: ActionItem) {
  const tier = (i: ActionItem) => (i.kind === 'owner' ? 0 : 1)
  return tier(a) - tier(b)
    || rankOf(a) - rankOf(b)
    || Number(b.overdue) - Number(a.overdue)
    || byDue(a.due, b.due)
    || a.ref.localeCompare(b.ref)
}

/**
 * Everything open that is waiting on Missional Marketing: roles with nobody confirmed,
 * every decision not yet made, their own tasks, and any of ours that is stopped waiting
 * on them. Pass empty lists for registers the reader cannot see.
 */
export function waitingOnClient({
  owners, decisions, tasks, today = new Date(),
}: {
  owners: Owner[]
  decisions: Decision[]
  tasks: Task[]
  today?: Date
}): ActionItem[] {
  const fromOwners = owners.filter((o) => !isSettled(o)).map((o): ActionItem => ({
    key: `owner:${o.id}`, kind: 'owner', ref: o.role,
    title: isNamed(o) ? `${o.name} has been asked but has not confirmed` : 'Nobody named yet',
    priority: 'Critical', status: o.status, due: OWNERS_DUE, dueText: '',
    overdue: pastDue(OWNERS_DUE, today), target: urlForKind('owner', o.id),
  }))

  const fromDecisions = decisions.filter((d) => d.status !== 'Decided').map((d): ActionItem => ({
    key: `decision:${d.decisionId}`, kind: 'decision', ref: d.decisionId, title: d.question,
    priority: d.priority, status: d.status, due: d.targetDate || null, dueText: '',
    overdue: isOverdue(d, today), target: urlForKind('decision', d.decisionId),
  }))

  const fromTasks = tasks
    .filter((t) => isOpenTask(t) && (t.owner !== 'Milestone' || t.status === 'Awaiting Client'))
    .map((t) => taskItem(t, today))

  return [...fromOwners, ...fromDecisions, ...fromTasks].sort(byUrgency)
}

const ACTIVE = new Set(['In Progress', 'In Review', 'Blocked'])

/**
 * Milestone's side of the board. `active` is the work that has started — blocked work
 * included, because it has started and it is ours to unstick — and `next` is what starts
 * after it. Work stopped waiting on the client is left out: it is on the other card.
 */
export function inFlight(tasks: Task[], today = new Date()): {
  active: ActionItem[]
  next: ActionItem[]
} {
  const ours = tasks.filter((t) => t.owner !== 'Client')
  const soonest = (a: ActionItem, b: ActionItem) =>
    Number(b.overdue) - Number(a.overdue) || byDue(a.due, b.due) || a.ref.localeCompare(b.ref)

  return {
    active: ours.filter((t) => ACTIVE.has(t.status)).map((t) => taskItem(t, today)).sort(soonest),
    next: ours.filter((t) => t.status === 'Not Started').map((t) => taskItem(t, today)).sort(soonest),
  }
}
